import { Fragment, useEffect } from 'react'
import { createPortal } from 'react-dom'

import { pesos } from '@/shared/utilidades/formato'
import { enLetras } from '@/shared/utilidades/enLetras'

import { EMPRESA } from './datosEmpresa'
import estilos from './HojaCotizacion.module.css'

/**
 * LA COTIZACIÓN COMO DOCUMENTO, NO COMO PANTALLA.
 *
 * Johan mandó la que emite World Office —COT 2773— y dijo: cuando le dé
 * imprimir en PDF debe quedar así. Y tiene razón en pedirlo: eso es lo que el
 * cliente recibe y lo que bodega lee para alistar el material. Una captura de
 * la pantalla de trabajo no sirve para ninguna de las dos cosas.
 *
 * Se dibuja con el mismo reparto que el documento de World Office: el membrete
 * y el número arriba, los datos del cliente en dos columnas, los renglones con
 * su IVA uno a uno, y al pie el valor en letras junto a la caja de totales.
 * Quien reciba los dos papeles tiene que ver el mismo documento.
 *
 * NO SE GENERA UN PDF: SE IMPRIME. El diálogo del navegador ofrece «Guardar
 * como PDF», así que el archivo sale de ahí. Un generador propio significaría
 * mantener dos maquetas —la de pantalla y la del PDF— y que un día dejen de
 * decir lo mismo.
 */

export type LineaHoja = {
  codigo: string | null
  descripcion: string
  cantidad: number
  unidad: string | null
  precioUnitario: number
  ivaTasa: number | null
  ivaValor: number
  total: number
  /** En el consolidado de la obra: de qué documento viene el renglón. */
  grupo?: string | null
}

export type DatosHoja = {
  /** «COTIZACION» si no se dice otra cosa; el consolidado lleva el suyo. */
  titulo?: string
  /** «FEV 2773», «COT-2026-0004»… tal como se le habla al cliente. */
  numero: string
  prefijo: string | null
  fecha: string | null
  cliente: string
  clienteDocumento: string | null
  clienteDireccion: string | null
  clienteTelefono: string | null
  clienteCiudad: string | null
  clienteCorreo: string | null
  vendedor: string | null
  concepto: string | null
  lineas: LineaHoja[]
  subtotal: number
  iva: number
  total: number
}

/** Igual que en el documento de World Office: 15-sept.-26. */
function fechaCorta(iso: string | null): string {
  if (!iso) return '—'

  const d = new Date(iso.length <= 10 ? iso + 'T12:00:00' : iso)
  if (Number.isNaN(d.getTime())) return '—'

  const mes = d.toLocaleDateString('es-CO', { month: 'short' }).replace('.', '')
  const dia = String(d.getDate()).padStart(2, '0')
  const anio = String(d.getFullYear()).slice(2)

  return dia + '-' + mes + '.-' + anio
}

/** Sin el signo de pesos: en la tabla del documento no lo lleva. */
function numero(v: number): string {
  return pesos(v).replace('$', '').trim()
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <p className={estilos.campo}>
      <span className={estilos.rotulo}>{rotulo}</span>
      <span className={estilos.valor}>{valor ?? ''}</span>
    </p>
  )
}

function Total({
  rotulo,
  valor,
  destacado,
}: {
  rotulo: string
  valor: number
  destacado?: boolean
}) {
  const clase = destacado ? estilos.total + ' ' + estilos.totalFinal : estilos.total

  return (
    <p className={clase}>
      <span>{rotulo}</span>
      <b>{numero(valor)}</b>
    </p>
  )
}

type PropsHoja = {
  datos: DatosHoja
  onCerrar: () => void
}

export function HojaCotizacion({ datos, onCerrar }: PropsHoja) {
  /*
   * MIENTRAS LA HOJA ESTÁ ABIERTA, LO QUE SE IMPRIME ES LA HOJA.
   *
   * La marca en el body la usa la regla de impresión para esconder la
   * aplicación entera. Sin esto saldrían las dos cosas: el documento y la
   * pantalla de trabajo detrás.
   */
  useEffect(() => {
    document.body.classList.add('hoja-abierta')
    return () => document.body.classList.remove('hoja-abierta')
  }, [])

  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
    }

    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [onCerrar])

  return createPortal(
    <div className={estilos.capa} role="dialog" aria-label={'Cotización ' + datos.numero}>
      <div className={estilos.mandos + ' no-imprimir'}>
        <button type="button" className={estilos.imprimir} onClick={() => window.print()}>
          Imprimir o guardar en PDF
        </button>
        <button type="button" className={estilos.cerrar} onClick={onCerrar}>
          Cerrar
        </button>
      </div>

      <article className={estilos.hoja}>
        <header className={estilos.membrete}>
          <img src="/marca/mega-logo.svg" alt="" className={estilos.logo} />

          <div className={estilos.empresa}>
            <p className={estilos.razonSocial}>{EMPRESA.nombre}</p>
            <p className={estilos.nit}>{EMPRESA.nit}</p>
            <p className={estilos.regimen}>{EMPRESA.regimen}</p>
            <p className={estilos.actividad}>{EMPRESA.actividad}</p>
          </div>

          <div className={estilos.folio}>
            <p className={estilos.folioTitulo}>{datos.titulo ?? 'COTIZACION'}</p>

            <p className={estilos.folioNumero}>
              {datos.prefijo && <span className={estilos.prefijo}>{datos.prefijo}</span>}
              <span className={estilos.no}>No.</span>
              <b>{datos.numero.replace(/^[A-Za-z]+[\s-]*/, '')}</b>
            </p>

            <p className={estilos.folioFila}>
              <span>FECHA</span>
              <b>{fechaCorta(datos.fecha)}</b>
            </p>

            <p className={estilos.folioFila}>
              <span>VENCIMIENTO</span>
              <b>{fechaCorta(datos.fecha)}</b>
            </p>
          </div>
        </header>

        <section className={estilos.cliente}>
          <div className={estilos.clienteIzq}>
            <Campo rotulo="CLIENTE" valor={datos.cliente} />
            <Campo rotulo="DIRECCIÓN" valor={datos.clienteDireccion} />
            <Campo rotulo="VENDEDOR" valor={datos.vendedor} />
            <Campo rotulo="E-MAIL" valor={datos.clienteCorreo} />
            <Campo rotulo="CONCEPTO" valor={datos.concepto} />
          </div>

          <div className={estilos.clienteDer}>
            <Campo rotulo="NIT" valor={datos.clienteDocumento} />
            <Campo rotulo="TELÉFONO" valor={datos.clienteTelefono} />
            <Campo rotulo="CIUDAD" valor={datos.clienteCiudad} />
            {/* VACÍO A PROPÓSITO. Estaba fijo en «Credito», copiado del
                documento de muestra. En una cotización eso es una condición
                de pago: decirla sin saberla es peor que dejarla en blanco,
                porque el cliente la lee como un acuerdo. El espejo de World
                Office no trae la forma de pago. */}
            <Campo rotulo="PAGO A" valor={null} />
          </div>
        </section>

        <table className={estilos.tabla}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Código</th>
              <th className={estilos.descripcion}>Descripción</th>
              <th>Cantidad</th>
              <th>U Med</th>
              {/* «sin IVA» escrito (OS-2026-0113): la mano de obra decía «V.
                  Unitario 100.000» y al facturar la tomaron como precio con
                  IVA; WO le quitó el IVA otra vez y quedó en 84.034. */}
              <th className={estilos.derecha}>V. Unit. sin IVA</th>
              {/* SIN LAS COLUMNAS DE IVA. Estaban porque el documento de World
                  Office las trae, pero en la mesa confunden mas de lo que
                  informan: «Valor IVA» era el IVA de UNA unidad —1.197 en un
                  renglon de 35 cables— al lado de un «Total» que va sin IVA.
                  Quien lee cruza las dos y no cuadra. El IVA sigue entero en
                  la caja de totales del pie, que es donde se cobra. */}
              <th className={estilos.derecha}>Total sin IVA</th>
            </tr>
          </thead>

          <tbody>
            {datos.lineas.map((l, i) => (
              <Fragment key={(l.codigo ?? 'x') + '-' + i}>
                {/* En el consolidado, un rótulo cada vez que empieza otro
                    documento: se sigue sabiendo qué vino de dónde. */}
                {l.grupo && l.grupo !== datos.lineas[i - 1]?.grupo && (
                  <tr className={estilos.grupo}>
                    <td colSpan={7}>{l.grupo}</td>
                  </tr>
                )}
                <tr>
                  <td className={estilos.centro}>{i + 1}</td>
                  <td>{l.codigo ?? '—'}</td>
                  <td className={estilos.descripcion}>{l.descripcion}</td>
                  <td className={estilos.centro}>{l.cantidad}</td>
                  <td className={estilos.centro}>{l.unidad ?? 'Und.'}</td>
                  <td className={estilos.derecha}>{numero(l.precioUnitario)}</td>
                  <td className={estilos.derecha}>{numero(l.total)}</td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>

        <section className={estilos.cierre}>
          <div className={estilos.letras}>
            <p className={estilos.letrasTitulo}>Valor en Letras</p>
            <p className={estilos.letrasTexto}>{enLetras(datos.total)}</p>
            <p className={estilos.cuantas}>Total líneas o ítems: {datos.lineas.length}</p>
          </div>

          <div className={estilos.totales}>
            <Total rotulo="SUBTOTAL" valor={datos.subtotal} />
            <Total rotulo="DESCUENTO" valor={0} />
            <Total rotulo="IVA" valor={datos.iva} />
            <Total rotulo="TOTAL" valor={datos.total} destacado />
          </div>
        </section>

        <footer className={estilos.pie}>
          <p className={estilos.condicionesTitulo}>CONDICIONES COMERCIALES:</p>
          <p className={estilos.condiciones}>{EMPRESA.condiciones}</p>
          <p className={estilos.banco}>{EMPRESA.banco}</p>
          <p className={estilos.contacto}>
            {EMPRESA.direccion} · Teléfono {EMPRESA.telefono} · {EMPRESA.ciudad}
          </p>
          <p className={estilos.contacto}>Correo Electrónico {EMPRESA.correo}</p>
        </footer>
      </article>
    </div>,
    document.body,
  )
}
